import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  CommentAuthorTypeSchema,
  ListMentionableUsersResponseSchema,
} from "../comments.contract";
import {
  As400ExportGroupSchema,
  isAs400WorksheetColumnKey,
  type As400ExportGroup,
} from "./products.contract";

// ─── Change-set cell comments (Item Master review, GEN-3261) ────────────────
// Excel-style comment threads on a review's worksheet cells. A thread is
// anchored to one cell — worksheet + row + column — and carries its messages
// and a resolved state. Several threads may sit on one cell. Threads are not
// part of change-set versions: restoring a version leaves them alone, and a
// thread whose row has left the review (edit reverted, new row deleted, product
// removed) still lists, flagged `orphaned`.

/** Grid row keys of a review's staged new rows: `new:<StagedNewRow id>`. */
export const CHANGE_SET_NEW_ROW_KEY_PREFIX = "new:";

/**
 * The row a comment sits on: an ingest row (the identity staged edits use) or
 * one of the review's staged new rows.
 */
export const ChangeSetCommentRowSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("data"),
    dataSourceLinkId: z.uuid(),
    dataId: z.uuid(),
  }),
  z.object({
    kind: z.literal("new"),
    stagedNewRowId: z.uuid(),
  }),
]);
export type ChangeSetCommentRow = z.infer<typeof ChangeSetCommentRowSchema>;

/**
 * The row's own key — `<linkId>:<dataId>` for an ingest row, `new:<id>` for a
 * staged new row. An ingest row's key is one segment of the grid's row key:
 * the grid keys an export row by joining its sources with `|`, so a Purchase
 * Data row built from two files is `<linkA>:<dataA>|<linkB>:<dataB>`.
 */
export function changeSetCommentRowKey(row: ChangeSetCommentRow): string {
  return row.kind === "new"
    ? `${CHANGE_SET_NEW_ROW_KEY_PREFIX}${row.stagedNewRowId}`
    : `${row.dataSourceLinkId}:${row.dataId}`;
}

/**
 * The row keys a grid row answers to: each of its sources' (one, or two for a
 * joined Purchase Data row), or its `new:<id>` key. Each ingest row is the
 * source of exactly one grid row, so a comment's row key names one grid row
 * however the files pair up.
 */
export function gridRowCommentRowKeys(gridRowKey: string): string[] {
  if (gridRowKey.startsWith(CHANGE_SET_NEW_ROW_KEY_PREFIX)) return [gridRowKey];
  return gridRowKey.split("|");
}

/**
 * The row to anchor a new comment on, read from the review grid's row key
 * (`As400ExportRow.key`, or `new:<id>`). A joined row anchors on its first
 * source; it still matches the joined row ({@link gridRowCommentRowKeys}).
 * Null for a key that names no saved row (an unsaved `draft:` row).
 */
export function changeSetCommentRowFromGridKey(
  gridRowKey: string,
): ChangeSetCommentRow | null {
  if (gridRowKey.startsWith(CHANGE_SET_NEW_ROW_KEY_PREFIX)) {
    const parsed = z
      .uuid()
      .safeParse(gridRowKey.slice(CHANGE_SET_NEW_ROW_KEY_PREFIX.length));
    return parsed.success ? { kind: "new", stagedNewRowId: parsed.data } : null;
  }
  const [dataSourceLinkId, dataId] = (
    gridRowCommentRowKeys(gridRowKey)[0] ?? ""
  ).split(":");
  const parsed = ChangeSetCommentRowSchema.safeParse({
    kind: "data",
    dataSourceLinkId,
    dataId,
  });
  return parsed.success ? parsed.data : null;
}

/**
 * One cell's comment key: worksheet + row key + column. The worksheet is part
 * of it because two worksheets can read the same ingest row. Opaque — build it
 * only through this function. `anchor.cellKey` holds a thread's.
 */
export function changeSetCommentCellKey(cell: {
  group: As400ExportGroup;
  rowKey: string;
  columnKey: string;
}): string {
  return `${cell.group}#${cell.rowKey}#${cell.columnKey}`;
}

/**
 * The cell keys a grid cell answers to — one per row key of its row
 * ({@link gridRowCommentRowKeys}). Index threads by `anchor.cellKey` and look
 * each of these up: the grid, the comments panel, both exports and Review
 * mode all match comments to cells this way.
 */
export function gridCellCommentKeys(
  group: As400ExportGroup,
  gridRowKey: string,
  columnKey: string,
): string[] {
  return gridRowCommentRowKeys(gridRowKey).map((rowKey) =>
    changeSetCommentCellKey({ group, rowKey, columnKey }),
  );
}

/** Where a new thread goes: one cell of a review worksheet. */
export const ChangeSetCommentAnchorInputSchema = z
  .object({
    /** The worksheet. */
    group: As400ExportGroupSchema,
    row: ChangeSetCommentRowSchema,
    /** The column's edit key (`as400ColumnEditKey`), or `AS400_CDB_COLUMN`. */
    columnKey: z.string().min(1).max(100),
    /** The row's item number and CDB key as the grid shows them; display only. */
    itemNumber: z.string().trim().max(100).nullable().optional(),
    cdb: z.string().trim().max(100).nullable().optional(),
  })
  .refine(
    ({ group, columnKey }) => isAs400WorksheetColumnKey(group, columnKey),
    {
      message: "Column is not a column of that worksheet",
      path: ["columnKey"],
    },
  );
export type ChangeSetCommentAnchorInput = z.infer<
  typeof ChangeSetCommentAnchorInputSchema
>;

/** A thread's cell, with what locating and labelling it needs. */
export const ChangeSetCommentAnchorSchema = z.object({
  group: As400ExportGroupSchema,
  /** The division (DataSource) the row belongs to. */
  dataSourceId: z.uuid(),
  row: ChangeSetCommentRowSchema,
  /** {@link changeSetCommentRowKey} of `row`. */
  rowKey: z.string(),
  columnKey: z.string(),
  /** {@link changeSetCommentCellKey} of the cell. */
  cellKey: z.string(),
  /** Captured when the thread was opened. */
  itemNumber: z.string().nullable(),
  cdb: z.string().nullable(),
});
export type ChangeSetCommentAnchor = z.infer<
  typeof ChangeSetCommentAnchorSchema
>;

const CommentBodySchema = z.string().trim().min(1).max(10000);

export const ChangeSetCommentMessageSchema = z.object({
  id: z.uuid(),
  authorType: CommentAuthorTypeSchema,
  authorUserId: z.uuid().nullable(),
  /** The author's current name; null when unknown. */
  authorName: z.string().nullable(),
  /** Plain text; `{user:<uuid>}` tokens mark @mentions. */
  body: z.string(),
  /**
   * The people the body @mentions who could read the review when it was last
   * written — the ones notified — with their current names (null when
   * unknown). A token for anyone else reads as an unknown user.
   */
  mentions: z.array(
    z.object({ userId: z.uuid(), name: z.string().nullable() }),
  ),
  createdAt: z.coerce.date(),
  /** When the author last edited it; null = never. */
  editedAt: z.coerce.date().nullable(),
});
export type ChangeSetCommentMessage = z.infer<
  typeof ChangeSetCommentMessageSchema
>;

export const ChangeSetCommentThreadSchema = z.object({
  id: z.uuid(),
  changeSetId: z.uuid(),
  anchor: ChangeSetCommentAnchorSchema,
  /** The anchor's row is no longer in the review. */
  orphaned: z.boolean(),
  resolvedAt: z.coerce.date().nullable(),
  resolvedByUserId: z.uuid().nullable(),
  resolvedByName: z.string().nullable(),
  createdByUserId: z.uuid().nullable(),
  createdAt: z.coerce.date(),
  /** Oldest first. Never empty: a thread whose messages were all deleted no longer lists. */
  messages: z.array(ChangeSetCommentMessageSchema),
});
export type ChangeSetCommentThread = z.infer<
  typeof ChangeSetCommentThreadSchema
>;

export const ChangeSetCommentThreadStatusSchema = z.enum([
  "open",
  "resolved",
  "all",
]);
export type ChangeSetCommentThreadStatus = z.infer<
  typeof ChangeSetCommentThreadStatusSchema
>;

export const ListChangeSetCommentThreadsInputSchema = z.object({
  changeSetId: z.uuid(),
  status: ChangeSetCommentThreadStatusSchema.prefault("all"),
  /** Only threads on this worksheet. */
  group: As400ExportGroupSchema.optional(),
  /** Only threads on this division's rows. */
  dataSourceId: z.uuid().optional(),
  /** Only threads this user wrote a message in. */
  authorId: z.uuid().optional(),
});
export type ListChangeSetCommentThreadsInput = z.input<
  typeof ListChangeSetCommentThreadsInputSchema
>;

export const ListChangeSetCommentThreadsResponseSchema = z.object({
  /** Oldest first. */
  threads: z.array(ChangeSetCommentThreadSchema),
});
export type ListChangeSetCommentThreadsResponse = z.infer<
  typeof ListChangeSetCommentThreadsResponseSchema
>;

export const CreateChangeSetCommentThreadInputSchema = z.object({
  changeSetId: z.uuid(),
  anchor: ChangeSetCommentAnchorInputSchema,
  body: CommentBodySchema,
});
export type CreateChangeSetCommentThreadInput = z.infer<
  typeof CreateChangeSetCommentThreadInputSchema
>;

export const ReplyToChangeSetCommentThreadInputSchema = z.object({
  threadId: z.uuid(),
  body: CommentBodySchema,
});
export type ReplyToChangeSetCommentThreadInput = z.infer<
  typeof ReplyToChangeSetCommentThreadInputSchema
>;

export const EditChangeSetCommentMessageInputSchema = z.object({
  messageId: z.uuid(),
  body: CommentBodySchema,
});
export type EditChangeSetCommentMessageInput = z.infer<
  typeof EditChangeSetCommentMessageInputSchema
>;

export const DeleteChangeSetCommentMessageInputSchema = z.object({
  messageId: z.uuid(),
});
export type DeleteChangeSetCommentMessageInput = z.infer<
  typeof DeleteChangeSetCommentMessageInputSchema
>;

export const ChangeSetCommentThreadIdInputSchema = z.object({
  threadId: z.uuid(),
});
export type ChangeSetCommentThreadIdInput = z.infer<
  typeof ChangeSetCommentThreadIdInputSchema
>;

export const ListChangeSetMentionableUsersInputSchema = z.object({
  changeSetId: z.uuid(),
});
export type ListChangeSetMentionableUsersInput = z.infer<
  typeof ListChangeSetMentionableUsersInputSchema
>;

/** The review (change set) a thread is on. */
export const LocateChangeSetCommentThreadResponseSchema = z.object({
  changeSetId: z.uuid(),
});
export type LocateChangeSetCommentThreadResponse = z.infer<
  typeof LocateChangeSetCommentThreadResponseSchema
>;

/**
 * The thread as it stands after the write; null once its last message is
 * deleted (the thread then no longer lists).
 */
export const ChangeSetCommentThreadResponseSchema = z.object({
  thread: ChangeSetCommentThreadSchema.nullable(),
});
export type ChangeSetCommentThreadResponse = z.infer<
  typeof ChangeSetCommentThreadResponseSchema
>;

export const changeSetCommentsContracts = {
  listThreads: oc
    .input(ListChangeSetCommentThreadsInputSchema)
    .output(ListChangeSetCommentThreadsResponseSchema)
    .route({
      method: "GET",
      summary: "List a review's cell comment threads",
      description:
        "Returns the comment threads on a product change set's worksheet cells, with their messages and cell anchors, optionally filtered by status, worksheet, division or author. Threads whose row has left the review are flagged as orphaned.",
    }),
  createThread: oc
    .input(CreateChangeSetCommentThreadInputSchema)
    .output(ChangeSetCommentThreadResponseSchema)
    .route({
      method: "POST",
      summary: "Comment on a review cell",
      description:
        "Starts a comment thread on one worksheet cell of a product change set, with its first message.",
    }),
  reply: oc
    .input(ReplyToChangeSetCommentThreadInputSchema)
    .output(ChangeSetCommentThreadResponseSchema)
    .route({
      method: "POST",
      summary: "Reply to a review cell comment",
      description: "Adds a message to a cell comment thread on a change set.",
    }),
  editMessage: oc
    .input(EditChangeSetCommentMessageInputSchema)
    .output(ChangeSetCommentThreadResponseSchema)
    .route({
      method: "POST",
      summary: "Edit your review cell comment",
      description:
        "Changes the text of a cell comment message you wrote; it is marked as edited.",
    }),
  deleteMessage: oc
    .input(DeleteChangeSetCommentMessageInputSchema)
    .output(ChangeSetCommentThreadResponseSchema)
    .route({
      method: "POST",
      summary: "Delete your review cell comment",
      description:
        "Deletes a cell comment message you wrote. A thread whose last message is deleted no longer lists.",
    }),
  resolveThread: oc
    .input(ChangeSetCommentThreadIdInputSchema)
    .output(ChangeSetCommentThreadResponseSchema)
    .route({
      method: "POST",
      summary: "Resolve a review cell comment thread",
      description:
        "Marks a cell comment thread on a change set as resolved, recording who resolved it.",
    }),
  reopenThread: oc
    .input(ChangeSetCommentThreadIdInputSchema)
    .output(ChangeSetCommentThreadResponseSchema)
    .route({
      method: "POST",
      summary: "Reopen a review cell comment thread",
      description: "Reopens a resolved cell comment thread on a change set.",
    }),
  locateThread: oc
    .input(ChangeSetCommentThreadIdInputSchema)
    .output(LocateChangeSetCommentThreadResponseSchema)
    .route({
      method: "GET",
      summary: "Find the review of a cell comment thread",
      description:
        "Returns the product change set (review) a cell comment thread is on, for links that name only the thread, such as an @mention notification's. Not found when the thread is not in your organization.",
    }),
  listMentionableUsers: oc
    .input(ListChangeSetMentionableUsersInputSchema)
    .output(ListMentionableUsersResponseSchema)
    .route({
      method: "GET",
      summary: "List users you can mention in a review's cell comments",
      description:
        "Returns the active users in your organization who can read the product change set (Product:Read), sorted by name, for @mentions in its cell comments. Only these users are notified when mentioned.",
    }),
} as const;
